/**
 * The fabric picker.
 *
 * Phase 1 of docs/decisions/fabric-picker-and-gating.md: choosing. Applying is
 * phase 2 and arrives through `onPick`.
 *
 * **Swatches are drawn at panel scale, and that is the whole design.** The
 * measurement in fabric-shapes-and-garments.md found that 26 of 39 photographic
 * fabrics are indistinguishable from a flat colour at 128px — the real size of a
 * torso panel. A grid of gorgeous 256px tiles would therefore be lying about two
 * thirds of the library: every swatch looks like fabric, and most of them land
 * as paint chips.
 *
 * So each swatch is tiled at the scale the fill will use, at roughly the size it
 * will occupy. The picker looks a little less seductive and is honest, and it
 * does the merchandising for free — the fabrics that survive downsampling are
 * visibly better in the grid, which is the order we want people choosing in.
 *
 * No search box. Forty items in six labelled groups is a grid you scan faster
 * than you type, and a search box would be an admission the grouping failed.
 */

import { DEFAULT_TILE_PX, loadTile, MAX_TILE_PX, MIN_TILE_PX, tilePattern } from "./fabric-fill";

export interface FabricEntry {
  id: string;
  name: string;
  group: string;
  tile: [number, number];
  authors: string[];
  source: string;
  licence: string;
  /** From the panel-scale measurement; "reads-as-flat-colour" for most. */
  panelVerdict?: string;
  panelContrast?: number;
}

export interface FabricPickerOptions {
  /** Chosen, at the tile width the swatches were showing. */
  onPick: (entry: FabricEntry, tilePx: number, tint: string | null) => void;
  /**
   * Show this fabric on the body, or clear it when null.
   *
   * Fires on hover and on focus, so the preview is reachable by keyboard too —
   * it is the whole reason to open this sheet, not a pointer flourish.
   */
  onPreview?: (entry: FabricEntry | null, tilePx: number, tint: string | null) => void;
  /** True when this fabric is outside the current plan. */
  isLocked?: (entry: FabricEntry) => boolean;
  /** A locked swatch was chosen. */
  onLocked?: (entry: FabricEntry) => void;
  /** False when this plan cannot recolour. The control shows, and explains. */
  canTint?: () => boolean;
  /** The colour control was used without the plan for it. */
  onTintLocked?: () => void;
}

/** Long enough to skip the swatches you slide across on the way to one. */
const PREVIEW_DELAY_MS = 90;

/** The size a torso panel occupies, and therefore the size a swatch is judged at. */
const SWATCH = 96;

const GROUP_ORDER = ["pattern", "denim", "leather", "knit", "woven", "smooth"];
const GROUP_LABEL: Record<string, string> = {
  pattern: "Patterns",
  denim: "Denim",
  leather: "Leather",
  knit: "Knits",
  woven: "Woven",
  smooth: "Smooth",
};

/**
 * A name that distinguishes the colourways.
 *
 * Several Poly Haven assets ship two or three colourways of one weave, and they
 * share the asset's `name` — so the grid showed "Book Pattern" three times with
 * three different swatches and no way to say which was which. The variant comes
 * off the id, which is the only place it exists.
 */
export function displayName(entry: { id: string; name: string }): string {
  /*
    "Fabric" is a category, not a name. Poly Haven prefixes half the library with
    it, which cost eleven characters of a cell that only fits about eighteen —
    so four adjacent swatches all read "Fabric Pattern 0…" and the colourway
    number they differ by was the part that got truncated away.
  */
  const base = entry.name.replace(/^Fabric\s+/i, "");
  const dash = entry.id.lastIndexOf("-");
  if (dash === -1) return base;
  const variant = entry.id.slice(dash + 1);
  if (variant === "page") return `${base} · Page`;
  // Only a suffix that actually looks like a colourway counts. Poly Haven spells
  // it col1, col_01 and coll1, and an asset id that merely contains a hyphen
  // must not be read as one — that produced "Some Fabric NaN".
  const match = /^col+0*(\d+)$/.exec(variant);
  return match ? `${base} · ${match[1]}` : base;
}

let sheet: HTMLDialogElement | null = null;

/**
 * Draw a swatch the way the fill will draw it.
 *
 * Tiled rather than stretched, at `FILL_SCALE`, so what is on screen is what
 * lands on the garment. Returns a canvas rather than an `<img>` because the tile
 * has to be repeated, and CSS `background-repeat` at a fractional size resamples
 * differently per browser — which would make the honest swatch dishonest again.
 */
function paintSwatch(
  canvas: HTMLCanvasElement,
  img: HTMLImageElement,
  dpr: number,
  tilePx: number,
  tint: string | null,
) {
  const size = Math.round(SWATCH * dpr);
  if (canvas.width !== size) {
    canvas.width = size;
    canvas.height = size;
  }
  const ctx = canvas.getContext("2d");
  if (!ctx) return;
  ctx.clearRect(0, 0, size, size);
  ctx.globalCompositeOperation = "source-over";

  // The same pattern call the fill uses, so a swatch and its result are the
  // same code rather than the same intention. It also matters for speed: at 16px
  // a hand-rolled loop is 36 draws per swatch and 1,400 across the grid, on
  // every drag of the slider.
  const pattern = tilePattern(ctx, img, tilePx, dpr);
  if (!pattern) return;

  if (tint) {
    // Base colour, then weave over it with overlay — mid-grey is identity, so
    // the colour survives exactly where the fabric has no texture to add.
    ctx.fillStyle = tint;
    ctx.fillRect(0, 0, size, size);
    ctx.globalCompositeOperation = "overlay";
  }
  ctx.fillStyle = pattern;
  ctx.fillRect(0, 0, size, size);
  ctx.globalCompositeOperation = "source-over";
}

export async function loadFabrics(): Promise<FabricEntry[]> {
  const res = await fetch("/textures/manifest.json");
  if (!res.ok) throw new Error(`fabric manifest: ${res.status}`);
  const data = (await res.json()) as { textures: FabricEntry[] };
  return data.textures;
}

export function createFabricPicker(opts: FabricPickerOptions) {
  let entries: FabricEntry[] | null = null;
  let building: Promise<void> | null = null;
  /*
    Scale lives here, not on the layer, because a fill is baked into pixels the
    moment it lands — there is nothing left to re-scale afterwards. Choosing it
    against the swatches means choosing it against the truth, at panel size,
    before committing.
  */
  let tilePx = DEFAULT_TILE_PX;
  /*
    The base colour, or null for "as it comes".

    Most of this library reads as a flat colour at panel size — that was the
    measurement, 26 of 39 — so for most fabrics the colour is all you are really
    choosing when you pick one. Setting it separately turns a library of 39
    fabrics into 39 weaves in any colour, and it is the difference between
    picking a brown leather and picking leather.
  */
  let tint: string | null = null;
  /** Every swatch, so a colour or scale change repaints without a re-render. */
  const painted: { canvas: HTMLCanvasElement; entry: FabricEntry }[] = [];

  /*
    Debounced, because a recomposite plus a texture upload per pointer sample is
    a stutter, and because sliding across a grid of forty swatches on the way to
    one should not preview thirty-nine fabrics.
  */
  let previewTimer = 0;
  let previewing: FabricEntry | null = null;
  /** Set once the sheet is built; re-reads the gate for the colour control. */
  let syncChip: () => void = () => {};

  function showPreview(entry: FabricEntry | null) {
    if (previewing === entry) return;
    previewing = entry;
    window.clearTimeout(previewTimer);
    if (!entry) {
      opts.onPreview?.(null, tilePx, tint);
      return;
    }
    previewTimer = window.setTimeout(() => opts.onPreview?.(entry, tilePx, tint), PREVIEW_DELAY_MS);
  }

  function repaintAll() {
    const dpr = window.devicePixelRatio || 1;
    const kind = tint ? "grain" : "colour";
    for (const { canvas, entry } of painted) {
      // loadTile caches per id and kind, so after the first pass this resolves
      // on the microtask queue and dragging the colour stays smooth.
      void loadTile(entry.id, kind).then((img) => paintSwatch(canvas, img, dpr, tilePx, tint));
    }
  }

  function build(): HTMLDialogElement {
    if (sheet) return sheet;
    const el = document.createElement("dialog");
    // Same primitive as the shortcuts sheet: <dialog> brings the focus trap,
    // Escape and the inert background from the platform rather than from us.
    el.className = "sheet floating fabric-sheet";
    el.setAttribute("aria-label", "Fabrics");

    const head = document.createElement("div");
    head.className = "sheet-head";
    const title = document.createElement("h2");
    title.className = "sheet-title";
    title.textContent = "Fabrics";
    const close = document.createElement("button");
    close.type = "button";
    close.className = "icon-btn";
    close.setAttribute("aria-label", "Close");
    close.textContent = "✕";
    close.addEventListener("click", () => {
      dismissed();
      el.close();
    });
    const scale = document.createElement("label");
    scale.className = "fabric-scale";
    const scaleName = document.createElement("span");
    scaleName.className = "fabric-control-label";
    scaleName.textContent = "Weave";
    const scaleText = document.createElement("span");
    scaleText.className = "fabric-scale-value";
    const slider = document.createElement("input");
    slider.type = "range";
    slider.min = String(MIN_TILE_PX);
    slider.max = String(MAX_TILE_PX);
    slider.step = "2";
    slider.value = String(tilePx);
    slider.setAttribute("aria-label", "Weave size");
    const setLabel = () => {
      // Just the number: "Weave" is already the label beside it, and repeating
      // the word cost enough width to wrap onto two lines.
      scaleText.textContent = `${tilePx} px`;
    };
    setLabel();
    slider.addEventListener("input", () => {
      tilePx = Number(slider.value);
      setLabel();
      repaintAll();
    });
    scale.appendChild(scaleName);
    scale.appendChild(slider);
    scale.appendChild(scaleText);

    /*
      The base colour.

      Two states, and the default is the honest one: "As it comes" shows each
      fabric's own albedo. Choosing a colour switches the whole grid to the
      grain variant tinted to it — so the library stops being 39 fabrics and
      becomes 39 weaves in whatever colour you are actually making.
    */
    const colour = document.createElement("div");
    colour.className = "fabric-tint";

    /*
      A native colour input, not the app's own picker.

      The picker renders its popover into the body, and this sheet is a modal
      `<dialog>` — everything outside a modal is inert and sits below it in the
      top layer, so an anchored popover in here is invisible and unclickable. A
      native input is browser chrome and immune to that. It is also the faster
      control for this job: one click to the OS palette, and it streams `input`
      events while dragging, which is what makes the whole grid recolour live.
    */
    const chip = document.createElement("input");
    chip.type = "color";
    chip.className = "fabric-chip";
    chip.value = "#0074BD";
    chip.setAttribute("aria-label", "Base colour");

    const colourName = document.createElement("span");
    colourName.className = "fabric-control-label";
    colourName.textContent = "Colour";

    /*
      A padlock, matching the swatches.

      Dimming alone reads as broken rather than gated — and a control that looks
      broken is one people stop clicking, which is the opposite of what a gate is
      for. The same glyph the locked swatches carry says "this has a price", not
      "this is out of order", before anybody clicks anything.
    */
    const colourLock = document.createElement("span");
    colourLock.className = "fabric-tint-lock";
    colourLock.setAttribute("aria-hidden", "true");
    colourLock.innerHTML = LOCK;

    const clear = document.createElement("button");
    clear.type = "button";
    clear.className = "fabric-tint-clear";
    clear.textContent = "As it comes";

    syncChip = () => {
      const locked = opts.canTint?.() === false;
      // Re-read every paint rather than once at build: the sheet outlives an
      // upgrade made in another tab, and a stale lock would refuse a customer
      // who has just paid.
      chip.disabled = locked;
      if (locked) chip.dataset.locked = "";
      else chip.removeAttribute("data-locked");
      colour.title = locked ? "Recolouring is part of Creator" : "";
      chip.title = locked ? "" : "Base colour";
      colourLock.hidden = !locked;
      // Named for what it is, so the control does not merely look different to
      // a screen reader — it says why.
      colourName.textContent = locked ? "Colour · Creator" : "Colour";
      /*
        An unset chip must not look like a chosen colour. A native colour input
        always renders *some* swatch, so the empty state is a slash across it —
        the same convention a colour picker uses for "none", and unmistakable
        next to a chip that has been set.
      */
      if (tint) chip.removeAttribute("data-empty");
      else chip.dataset.empty = "true";
      clear.hidden = !tint;
      colourName.textContent = tint ? "Colour" : "Colour";
    };
    syncChip();

    clear.addEventListener("click", () => {
      tint = null;
      syncChip();
      repaintAll();
    });
    /*
      Disabled, not intercepted.

      The first version cancelled `pointerdown` and let the input stand. That
      does not reliably stop Chrome handing off to the OS palette, so the palette
      opened, a colour was chosen, and the `input` handler declined it in
      silence — which from the outside is not a locked feature, it is a broken
      one. A disabled input cannot open a palette at all, so there is no path
      that ends in nothing happening.

      The refusal then belongs to the wrapper, which is still clickable and can
      say why.
    */
    chip.addEventListener("input", () => {
      if (opts.canTint?.() === false) return;
      tint = chip.value;
      syncChip();
      repaintAll();
    });

    colour.addEventListener("click", () => {
      if (opts.canTint?.() === false) opts.onTintLocked?.();
    });

    colour.appendChild(colourName);
    colour.appendChild(chip);
    colour.appendChild(colourLock);
    colour.appendChild(clear);

    head.appendChild(title);
    head.appendChild(colour);
    head.appendChild(scale);
    head.appendChild(close);
    el.appendChild(head);

    const body = document.createElement("div");
    body.className = "sheet-body fabric-body";
    el.appendChild(body);

    /*
      Every dismissal clears the preview, and none of them trusts `close`.

      The tidy version hung this on the dialog's own `close` event, on the
      grounds that Escape, the backdrop and the ✕ all end there. Measured
      directly, a plain <dialog> in this environment opens with showModal() and
      never fires `close` at all — so the tidy version left the previewed fabric
      on the body after the sheet went away, which looks exactly like having
      applied it.

      `close` is still listened for, because where it does fire it is the most
      complete signal. It is just no longer the only one.
    */
    const dismissed = () => showPreview(null);
    el.addEventListener("close", dismissed);
    el.addEventListener("cancel", dismissed); // Escape
    el.addEventListener("click", (e) => {
      if (e.target !== el) return;
      dismissed();
      el.close();
    });
    document.body.appendChild(el);
    sheet = el;
    return el;
  }

  function render(body: HTMLElement, list: FabricEntry[]) {
    const dpr = window.devicePixelRatio || 1;
    const byGroup = new Map<string, FabricEntry[]>();
    for (const e of list) {
      const g = byGroup.get(e.group);
      if (g) g.push(e);
      else byGroup.set(e.group, [e]);
    }
    // Known groups in the order they are taught, then anything the manifest
    // gained since — a new group appearing at the end beats it not appearing.
    const ordered = [
      ...GROUP_ORDER.filter((g) => byGroup.has(g)),
      ...[...byGroup.keys()].filter((g) => !GROUP_ORDER.includes(g)).sort(),
    ];

    painted.length = 0;
    body.replaceChildren();
    for (const group of ordered) {
      const section = document.createElement("section");
      section.className = "fabric-group";
      const h = document.createElement("h3");
      h.textContent = GROUP_LABEL[group] ?? group;
      section.appendChild(h);

      const grid = document.createElement("div");
      grid.className = "fabric-grid";
      for (const entry of byGroup.get(group) ?? []) {
        const locked = opts.isLocked?.(entry) ?? false;
        const button = document.createElement("button");
        button.type = "button";
        button.className = "fabric-swatch";
        if (locked) button.dataset.locked = "";
        // The name is the accessible name; the visible label sits under it.
        const shown = displayName(entry);
        button.setAttribute("aria-label", locked ? `${shown} — locked` : shown);

        const canvas = document.createElement("canvas");
        canvas.className = "fabric-canvas";
        button.appendChild(canvas);

        painted.push({ canvas, entry });
        void loadTile(entry.id, tint ? "grain" : "colour").then((img) =>
          paintSwatch(canvas, img, dpr, tilePx, tint),
        );

        const label = document.createElement("span");
        label.className = "fabric-name";
        label.textContent = shown;
        // The shortened name is for scanning; the full one still has to be
        // reachable, so it goes on the title where a hover or long-press finds it.
        button.title = entry.name;
        button.appendChild(label);

        if (locked) {
          const lock = document.createElement("span");
          lock.className = "fabric-lock";
          lock.setAttribute("aria-hidden", "true");
          lock.innerHTML = LOCK;
          button.appendChild(lock);
        }

        button.addEventListener("pointerenter", () => showPreview(entry));
        button.addEventListener("pointerleave", () => showPreview(null));
        button.addEventListener("focus", () => showPreview(entry));
        button.addEventListener("blur", () => showPreview(null));
        button.addEventListener("click", () => {
          // Locked swatches stay fully visible and fully previewable — greyed
          // things are invisible, and desire is the only thing that converts.
          if (locked) {
            opts.onLocked?.(entry);
            return;
          }
          /*
            The sheet stays open. Choosing a fabric is a comparison, not a
            commitment — you pick one, look at the body, and try the next. It is
            anchored clear of the 3D pane precisely so that loop works, and
            closing after every pick would break it.
          */
          showPreview(null);
          // Scoped to the whole sheet, not to `grid`: the previous pick is
          // usually in a different group, and clearing only the current one
          // left every group showing its own "applied" swatch.
          for (const el of body.querySelectorAll("[data-applied]")) {
            el.removeAttribute("data-applied");
          }
          button.dataset.applied = "";
          opts.onPick(entry, tilePx, tint);
        });
        grid.appendChild(button);
      }
      section.appendChild(grid);
      body.appendChild(section);
    }
  }

  return {
    /*
      Re-read the gate without reopening.

      Someone upgrades in the tab this modal sent them to and comes back to a
      sheet built when they were on free — every swatch still padlocked, the
      colour control still dead. Nothing they can do in this sheet would fix it,
      which reads as having paid for nothing.
    */
    refresh() {
      if (!sheet?.open || !entries) return;
      const body = sheet.querySelector<HTMLElement>(".fabric-body");
      if (body) render(body, entries);
      syncChip();
    },

    async open() {
      const el = build();
      const body = el.querySelector<HTMLElement>(".fabric-body");
      if (!body) return;

      if (!entries && !building) {
        building = loadFabrics()
          .then((list) => {
            entries = list;
          })
          .catch((err) => {
            body.textContent = "Could not load the fabric library.";
            console.error("fabrics", err);
          })
          .finally(() => {
            building = null;
          });
      }
      if (!el.open) el.showModal();
      await building;
      if (entries) render(body, entries);
    },
    close() {
      sheet?.close();
    },
  };
}

const LOCK =
  '<svg viewBox="0 0 24 24" class="icon icon-sm" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="4" y="11" width="16" height="10" rx="2"/><path d="M8 11V7a4 4 0 0 1 8 0v4"/></svg>';
